import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { crmApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { dataTablePageProps, usePagination } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import type { CrmDeal, CrmPipeline } from '@/types/crm';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export function CrmDealsPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('CRM_MANAGE', 'CRM_PIPELINE_MANAGE');
  const pagination = usePagination(50);
  const [deals, setDeals] = useState<CrmDeal[]>([]);
  const [kanbanDeals, setKanbanDeals] = useState<CrmDeal[]>([]);
  const [pipeline, setPipeline] = useState<CrmPipeline | null>(null);
  const [dragDealId, setDragDealId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dealPage, kanbanPage, pipe] = await Promise.all([
        crmApi.listDeals({ page: pagination.page, size: pagination.size }),
        crmApi.listDeals({ page: 0, size: 500, search: undefined }),
        crmApi.ensureDefaultPipeline().catch(() => crmApi.listPipelines().then((p) => p[0])),
      ]);
      setDeals(dealPage.content);
      setKanbanDeals(kanbanPage.content.filter((d) => d.dealStatus === 'OPEN'));
      pagination.applyPageResponse(dealPage);
      setPipeline(pipe ?? null);
    } catch (err) {
      showApiError(err, 'Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const stages = pipeline?.stages ?? [];

  const moveDeal = async (dealId: number, stageId: number) => {
    const deal = kanbanDeals.find((d) => d.id === dealId);
    if (deal?.stageId === stageId) return;
    try {
      await crmApi.moveDealStage(dealId, stageId);
      await load();
    } catch (e) {
      showApiError(e, 'Move deal failed');
    }
  };

  const onDropStage = (stageId: number) => {
    if (dragDealId == null) return;
    void moveDeal(dragDealId, stageId);
    setDragDealId(null);
  };

  const createQuote = async (dealId: number) => {
    try {
      const invoiceId = await crmApi.createDealQuote(dealId);
      notify.success(`Quote created (invoice #${invoiceId})`);
      await load();
    } catch (e) {
      showApiError(e, 'Create quote failed');
    }
  };

  const tableColumns: Column<CrmDeal>[] = [
    { key: 'dealName', header: 'Deal' },
    { key: 'customerName', header: 'Customer', render: (d) => d.customerName || '—' },
    { key: 'expectedRevenue', header: 'Revenue' },
    { key: 'probabilityPercentage', header: 'Probability' },
    { key: 'dealStatus', header: 'Status' },
    {
      key: 'financeInvoiceId',
      header: 'Finance',
      render: (d) =>
        d.financeInvoiceId ? (
          <Link to="/finance/invoices" className="text-primary-600 text-xs hover:underline">
            Invoice #{d.financeInvoiceId}
          </Link>
        ) : d.dealStatus === 'OPEN' && canManage && d.customerId ? (
          <button type="button" className="text-primary-600 text-xs hover:underline" onClick={() => void createQuote(d.id)}>
            Create quote
          </button>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Deals"
        description="Pipeline kanban with drag-and-drop stage moves"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'CRM', to: '/crm' }, { label: 'Deals' }]}
        actions={
          canManage ? (
            <Button
              onClick={() =>
                crmApi
                  .createDeal({
                    dealName: `Deal ${new Date().toLocaleDateString()}`,
                    expectedRevenue: 0,
                    probabilityPercentage: 10,
                    dealStatus: 'OPEN',
                    pipelineId: pipeline?.id,
                    stageId: stages[0]?.id,
                  })
                  .then(() => load())
                  .catch((e) => showApiError(e, 'Create deal failed'))
              }
            >
              New deal
            </Button>
          ) : undefined
        }
      />

      <div className="flex gap-3 overflow-x-auto pb-4">
        {stages.map((stage) => (
          <div
            key={stage.id}
            className="min-w-[240px] flex-shrink-0 rounded-lg border p-3 bg-slate-50 shadow-sm dark:bg-slate-900/40"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDropStage(stage.id)}
          >
            <h3 className="font-semibold text-sm mb-2">{stage.name}</h3>
            <ul className="space-y-2 min-h-[80px]">
              {kanbanDeals
                .filter((d) => d.stageId === stage.id)
                .map((d) => (
                  <li
                    key={d.id}
                    draggable={canManage}
                    onDragStart={() => canManage && setDragDealId(d.id)}
                    className="bg-white dark:bg-slate-800 rounded border p-2 text-sm shadow-sm cursor-grab active:cursor-grabbing hover:border-primary-300"
                  >
                    <p className="font-medium">{d.dealName}</p>
                    <p className="text-slate-500 text-xs">{d.customerName || 'No customer'}</p>
                    <p className="text-xs mt-1">{d.expectedRevenue}</p>
                    {d.financeInvoiceId && (
                      <p className="text-xs text-primary-600 mt-1">Invoice #{d.financeInvoiceId}</p>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <h3 className="font-semibold mb-3">All deals</h3>
        <DataTable
          columns={tableColumns}
          data={deals}
          loading={loading}
          keyField="id"
          {...dataTablePageProps(pagination)}
        />
      </div>
    </div>
  );
}
